import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  SxProps,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { colors } from '../utils/constants';
import { createRole, editRoleById } from '../api/requests/roles';
import useModuleActions from '../hooks/useModuleActions';
import { useAlert } from '../context/AlertContext';
import { labelStyles } from '../pages/createUser';
import CommonDialog from './common/CommonDialog';

interface ModuleAction {
  module: string;
  actions: string[];
}

interface SelectedValue {
  module: string;
  actions: string[];
}

interface Role {
  id: number;
  name: string;
  description?: string;
  permissions: SelectedValue[];
}

interface CreateRoleModalProps {
  open: boolean;
  handleClose: () => void;
  mode?: 'add' | 'edit';
  selectedRole?: Role | null;
  roleData?: any;
}

interface ErrorState {
  rank: string;
  modules: string;
}

const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  open,
  handleClose,
  mode,
  selectedRole,
  roleData,
}) => {
  const { showAlert } = useAlert();
  const [roleTitle, setRoleTitle] = useState<string>('');
  const [roleDescription, setRoleDescription] = useState<string>('');
  const [errors, setErrors] = useState<ErrorState>({ rank: '', modules: '' });
  const { moduleActions }: { moduleActions: ModuleAction[] } =
    useModuleActions();
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);

  useEffect(() => {
    if (mode === 'edit' && (roleData || selectedRole)) {
      // Edit mode: Load saved permissions
      const data = roleData || selectedRole;
      setRoleTitle(data?.roleName || data?.name || '');
      setRoleDescription(data?.description || '');
      setSelectedValues(data?.permissions || []);
      setErrors({ rank: '', modules: '' });
    } else {
      // Add mode: Select all permissions by default
      setRoleTitle('');
      setRoleDescription('');
      setSelectedValues(
        moduleActions.map((module) => ({
          module: module.module,
          actions: [...module.actions], // Select all actions for the module
        }))
      );
      setErrors({ rank: '', modules: '' });
    }
  }, [mode, roleData, selectedRole, open, moduleActions]);

  const validateForm = (): boolean => {
    const newErrors: ErrorState = { rank: '', modules: '' };
    let isValid = true;

    if (!roleTitle.trim()) {
      newErrors.rank = 'Role name is required';
      isValid = false;
    }

    if (selectedValues.length === 0) {
      newErrors.modules = 'At least one module must be selected';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const isChecked = (module: string, action: string): boolean => {
    return (
      selectedValues
        .find((item) => item.module === module)
        ?.actions.includes(action) || false
    );
  };

  const handleCheckboxChange = (module: string, action: string) => {
    const moduleIndex = selectedValues.findIndex(
      (item) => item.module === module
    );
    const newValues = [...selectedValues];

    if (moduleIndex !== -1) {
      const actions = new Set(newValues[moduleIndex].actions);
      if (actions.has(action)) {
        actions.delete(action);
      } else {
        actions.add(action);
      }
      newValues[moduleIndex].actions = Array.from(actions);
    } else {
      newValues.push({ module, actions: [action] });
    }
    const filtered = newValues.filter((item) => item.actions.length > 0);
    setSelectedValues(filtered);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    const payload = {
      roleName: roleTitle,
      permissions: selectedValues,
      description: roleDescription,
    };
    try {
      if (mode === 'edit' && (roleData?.roleId || selectedRole?.id)) {
        const roleId = (roleData?.roleId ?? selectedRole?.id ?? '').toString();
        await editRoleById(roleId, payload);
        showAlert('Role updated successfully', 'success');
      } else {
        await createRole(payload);
        showAlert('Role created successfully', 'success');
      }
      handleClose();
    } catch (error: any) {
      console.error(
        `Error ${mode === 'edit' ? 'updating' : 'creating'} role:`,
        error
      );
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${mode === 'edit' ? 'update' : 'create'} role`;
      showAlert(errorMessage, 'error');
    }
  };

  return (
    <CommonDialog
      title={mode === 'edit' ? 'Edit Role' : 'Add Role'}
      hideActionButtons={true}
      minWidth={720}
      cancelButton={handleClose}
    >
      <Box sx={boxStyles}>
        <Box sx={roleTextDescSx}>
          <Box sx={roleWrapperSx}>
            <Typography variant='body1' sx={subTitleStyles}>
              Role Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder='Enter Role'
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              error={!!errors.rank}
              helperText={errors.rank}
              slotProps={{
                input: {
                  sx: labelStyles,
                },
              }}
            />
          </Box>
          <Box sx={roleWrapperSx}>
            <Typography variant='body1' sx={subTitleStyles}>
              Description
            </Typography>
            <TextField
              fullWidth
              placeholder='Enter Role Description'
              value={roleDescription}
              maxRows={3}
              onChange={(e) => setRoleDescription(e.target.value)}
              slotProps={{
                input: {
                  sx: labelStyles,
                },
              }}
            />
          </Box>
          <Box sx={roleWrapperSx}>
            <Typography variant='body1' sx={subTitleStyles}>
              Select Modules *
            </Typography>
            <Box
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {moduleActions &&
                moduleActions.map((module, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Box sx={serialNumTextWrapperSx}>
                      <Box sx={serialNumSx}>{index + 1}</Box>
                      <Typography sx={subTitleStyles}>
                        {module.module}
                      </Typography>
                    </Box>
                    <Paper variant='outlined' sx={paperSx}>
                      <Grid
                        container
                        spacing={0.5}
                        sx={{
                          '&.MuiGrid-root': {
                            minWidth: '30%',
                          },
                        }}
                      >
                        {' '}
                        {/* Reduce spacing between items */}
                        {module.actions.map((action) => (
                          <Grid
                            item
                            key={action}
                            xs={6}
                            sm={4}
                            md={3}
                            sx={{ px: 0.25, py: 0.25 }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked(module.module, action)}
                                  onChange={() =>
                                    handleCheckboxChange(module.module, action)
                                  }
                                  sx={{
                                    p: 0.5,
                                    color: colors.indigoBlueColor,
                                    '&.Mui-checked': {
                                      color: colors.indigoBlueColor,
                                    },
                                    transform: 'scale(0.9)',

                                    '& .MuiSvgIcon-root': {
                                      fontSize: 18,
                                    },
                                  }}
                                />
                              }
                              label={action}
                              sx={{
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '12px',
                                  color: '#343434',
                                  fontWeight: 400,
                                },
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
        {errors.modules && (
          <Typography color='error' sx={{ mt: 1 }}>
            {errors.modules}
          </Typography>
        )}
        <Box sx={footerStyles}>
          <Button variant='outlined' sx={cancelButtonSx} onClick={handleClose}>
            Cancel
          </Button>
          <Button sx={okButtonSx} onClick={handleSubmit}>
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </CommonDialog>
  );
};

export default CreateRoleModal;

const buttonStyles: SxProps = {
  borderRadius: '8px',
  textTransform: 'inherit',
  height: '40px',
};

const okButtonSx: SxProps = {
  ...buttonStyles,
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
};
const cancelButtonSx: SxProps = {
  ...buttonStyles,
  backgroundColor: '#ffffff',
  border: '0.5px solid #848484',
  color: '#848484',
};

const boxStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  p: 2,
};

export const subTitleStyles: SxProps = {
  fontSize: '14px',
  fontWeight: 400,
  color: '#343434',
};

const footerStyles: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  height: '47px',
  mt: 2,
  gap: 2,
};

const roleTextDescSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const roleWrapperSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const serialNumSx: SxProps = {
  width: 45,
  height: 45,
  borderRadius: '50%',
  backgroundColor: '#F3F3F3',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 400,
  fontSize: '14px',
  color: '#343434',
};

const serialNumTextWrapperSx: SxProps = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  width: '26%',
};

const paperSx: SxProps = {
  border: '0.5px solid #9EC8FA',
  borderRadius: 1,
  padding: '10px',
  pl: 5,
  width: '73%',
};
